import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import client from '../API/API';
import AtomsLogo from '../assets/AtomsLogo.png';
import AtomsMark from '../assets/A_favicon_io/android-chrome-192x192.png';
import '../styles/tokens.css';

// ── Icons (inline SVG, Lucide-style) ─────────────────────────────────────────
const Icon = ({ d, size = 18, color = 'currentColor', strokeWidth = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const ICONS = {
  dashboard: 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z M9 22V12h6v10',
  machine:   'M12 15a3 3 0 100-6 3 3 0 000 6z M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
  trail:     'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  report:    'M18 20V10 M12 20V4 M6 20v-6',
  admin:     'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  superadmin:'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  menu:      'M3 12h18 M3 6h18 M3 18h18',
  bell:      'M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 01-3.46 0',
  user:      'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 11a4 4 0 100-8 4 4 0 000 8z',
  logout:    'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
  search:    'M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z',
  spark:     'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
};

const SidebarIcon = ({ d }) => <Icon d={d} size={17} />;

// ── Sidebar nav items ─────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: 1, path: '/app/dashboard', icon: ICONS.dashboard, label: 'Dashboard' },
  { id: 2, path: '/app/machine',   icon: ICONS.machine,   label: 'Machines'  },
  { id: 3, path: '/app/trail',     icon: ICONS.trail,     label: 'Trail'     },
  { id: 4, path: '/app/report',    icon: ICONS.report,    label: 'Report'    },
];

// ── User avatar initial ───────────────────────────────────────────────────────
const UserAvatar = ({ name = '', onClick }) => {
  const initial = (name[0] || 'U').toUpperCase();
  return (
    <button
      onClick={onClick}
      style={{
        width: 32, height: 32, borderRadius: '50%',
        background: 'var(--accent-soft)', color: 'var(--accent)',
        border: '1.5px solid var(--border-subtle)',
        fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 13,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all var(--t-fast)',
        flexShrink: 0,
      }}
    >
      {initial}
    </button>
  );
};

// ── Main Layout ───────────────────────────────────────────────────────────────
export default function MainLayout() {
  const navigate  = useNavigate();
  const location  = useLocation();

  // Sidebar collapsed state — persisted per RD-12
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('atoms_sb') === 'collapsed'; }
    catch { return false; }
  });

  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  const username   = localStorage.getItem('username') || 'User';
  const role       = localStorage.getItem('role') || 'user';
  const orgName    = localStorage.getItem('org_name') || 'ATOMS';
  const orgLogoUrl = localStorage.getItem('org_logo_url') || '';

  // RD-10: apply org_color as white-label accent
  useEffect(() => {
    const orgColor = localStorage.getItem('org_color');
    if (orgColor && /^#[0-9A-Fa-f]{6}$/.test(orgColor)) {
      const root = document.documentElement;
      root.style.setProperty('--accent', orgColor);
      // Derive soft / hover variants
      root.style.setProperty('--accent-soft', `${orgColor}1A`);  // 10% opacity
      root.style.setProperty('--accent-hover', orgColor + 'CC'); // darken via filter is CSS only; use as-is
    }
  }, []);

  const allowedPages = (() => {
    try { return JSON.parse(localStorage.getItem('pages') || '[]'); } catch { return []; }
  })();

  const sidebarItems = NAV_ITEMS.filter(i => allowedPages.includes(i.id));
  const showAdmin      = role === 'oem_admin' || role === 'distributor_admin';
  const showSuperAdmin = role === 'atoms_admin';

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem('atoms_sb', next ? 'collapsed' : 'expanded'); } catch {}
  };

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    try {
      const refresh = localStorage.getItem('refresh_token');
      await client.post('/api/logout/', { refresh });
    } catch {}
    localStorage.clear();
    navigate('/login');
  };

  const sw = collapsed ? 'var(--sidebar-w-sm)' : 'var(--sidebar-w)';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-app)' }}>

      {/* ── Sidebar ── */}
      <aside style={{
        width: sw,
        minWidth: sw,
        maxWidth: sw,
        background: 'var(--bg-surface)',
        borderRight: '1px solid var(--border-hair)',
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        height: '100vh',
        transition: 'width var(--t-normal), min-width var(--t-normal), max-width var(--t-normal)',
        zIndex: 100,
        overflow: 'hidden',
      }}>

        {/* Brand header */}
        <div style={{
          height: 'var(--topbar-h)',
          borderBottom: '1px solid var(--border-hair)',
          display: 'flex', alignItems: 'center',
          padding: collapsed ? '0 14px' : '0 16px',
          gap: 10, flexShrink: 0,
          overflow: 'hidden',
        }}>
          <img src={orgLogoUrl || AtomsMark} alt={orgName} style={{ height: 28, width: 28, objectFit: 'contain', flexShrink: 0, borderRadius: orgLogoUrl ? 4 : 0 }} />
          {!collapsed && (
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 12, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {orgName}
              </div>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
                {role.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </div>
            </div>
          )}
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: '8px 6px', overflow: 'hidden' }}>
          {sidebarItems.map(item => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <NavLink key={item.id} to={item.path} style={{ textDecoration: 'none', display: 'block' }}>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  gap: 10,
                  height: 38,
                  padding: collapsed ? '0 10px' : '0 10px',
                  borderRadius: 'var(--r-md)',
                  margin: '2px 0',
                  color: isActive ? 'var(--accent)' : 'var(--ink-600)',
                  background: isActive ? 'var(--accent-soft)' : 'transparent',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  position: 'relative',
                  borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
                }}
                  onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'var(--border-subtle)'; e.currentTarget.style.color = 'var(--ink-900)'; } }}
                  onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ink-600)'; } }}
                >
                  <SidebarIcon d={item.icon} />
                  {!collapsed && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>}
                </div>
              </NavLink>
            );
          })}

          {/* Admin links */}
          {(showAdmin || showSuperAdmin) && (
            <div style={{ margin: '12px 0 4px', borderTop: '1px solid var(--border-hair)', paddingTop: 8 }}>
              <NavLink
                to={showSuperAdmin ? '/app/super-admin' : '/app/admin-panel'}
                style={{ textDecoration: 'none', display: 'block' }}
              >
                {(() => {
                  const isActive = location.pathname.includes('admin');
                  return (
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      gap: 10,
                      height: 38,
                      padding: '0 10px',
                      borderRadius: 'var(--r-md)',
                      borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
                      color: isActive ? 'var(--accent)' : 'var(--ink-600)',
                      background: isActive ? 'var(--accent-soft)' : 'transparent',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      transition: 'all 0.15s ease',
                    }}
                      onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'var(--border-subtle)'; e.currentTarget.style.color = 'var(--ink-900)'; } }}
                      onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ink-600)'; } }}
                    >
                      <SidebarIcon d={showSuperAdmin ? ICONS.superadmin : ICONS.admin} />
                      {!collapsed && <span>{showSuperAdmin ? 'Super Admin' : 'Admin'}</span>}
                    </div>
                  );
                })()}
              </NavLink>
            </div>
          )}
        </nav>

        {/* Footer */}
        {!collapsed && (
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid var(--border-hair)',
            flexShrink: 0,
          }}>
            <img src={AtomsLogo} alt="ATOMS" style={{ height: 22 }} />
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 9, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>
              POWERED BY ATOMS
            </div>
          </div>
        )}
      </aside>

      {/* ── Main content ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>

        {/* Top bar */}
        <header style={{
          height: 'var(--topbar-h)',
          background: 'var(--bg-surface)',
          borderBottom: '1px solid var(--border-hair)',
          display: 'flex', alignItems: 'center',
          padding: '0 20px',
          gap: 12,
          position: 'sticky', top: 0, zIndex: 50,
          backdropFilter: 'blur(10px)',
          flexShrink: 0,
        }}>
          {/* Hamburger */}
          <button
            onClick={toggleCollapsed}
            style={{
              background: 'none', border: 'none',
              cursor: 'pointer', color: 'var(--ink-500)',
              padding: 6, borderRadius: 'var(--r-md)',
              display: 'flex', alignItems: 'center',
              transition: 'all var(--t-fast)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <Icon d={ICONS.menu} size={19} />
          </button>

          {/* ⌘K command bar */}
          <CommandBar />

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Live clock */}
          <LiveClock />

          {/* Bell */}
          <button style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--ink-500)', padding: 6, borderRadius: 'var(--r-md)',
            display: 'flex', alignItems: 'center',
            transition: 'background var(--t-fast)',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <Icon d={ICONS.bell} size={18} />
          </button>

          {/* User menu */}
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <UserAvatar name={username} onClick={() => setShowUserMenu(v => !v)} />
            <AnimatePresence>
              {showUserMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                  style={{
                    position: 'absolute', top: 40, right: 0,
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 'var(--r-lg)',
                    boxShadow: 'var(--sh-lg)',
                    minWidth: 180,
                    padding: '6px 0',
                    zIndex: 200,
                  }}
                >
                  <div style={{ padding: '8px 14px 10px', borderBottom: '1px solid var(--border-hair)' }}>
                    <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 13, color: 'var(--ink-900)' }}>{username}</div>
                    <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-500)', marginTop: 2 }}>{role.replace('_', ' ')}</div>
                  </div>
                  <button
                    onClick={handleLogout}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '9px 14px',
                      background: 'none', border: 'none',
                      fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--offline)',
                      cursor: 'pointer', transition: 'background var(--t-fast)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--offline-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    <Icon d={ICONS.logout} size={15} color="var(--offline)" />
                    Sign out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, overflow: 'auto', padding: 'var(--gut)' }}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={{ minHeight: '100%' }}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
}

// ── ⌘K Command Bar ────────────────────────────────────────────────────────────
function CommandBar() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setOpen(v => !v); }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--r-lg)', padding: '6px 14px',
          cursor: 'pointer', transition: 'all var(--t-fast)',
          minWidth: 240,
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = 'var(--sh-glow)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
      >
        <Icon d={ICONS.spark} size={14} color="var(--ai)" />
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-300)', flex: 1, textAlign: 'left' }}>
          Ask your machines, or jump to one…
        </span>
        <kbd style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-300)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, padding: '2px 5px' }}>⌘K</kbd>
      </button>
      <AnimatePresence>
        {open && <AskPalette onClose={() => setOpen(false)} />}
      </AnimatePresence>
    </>
  );
}

// ── Ask palette modal ─────────────────────────────────────────────────────────
function AskPalette({ onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const machines = (() => {
    try {
      const ws = JSON.parse(sessionStorage.getItem('atoms_machines') || '[]');
      return ws;
    } catch { return []; }
  })();

  const filtered = machines.filter(m =>
    !query || m.Machine_name?.toLowerCase().includes(query.toLowerCase()) || m.Machine_ID?.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(12,14,22,.32)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 120 }}
    >
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.2 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg-surface)', borderRadius: 'var(--r-xl)', boxShadow: 'var(--sh-lg)', width: '100%', maxWidth: 560, overflow: 'hidden' }}
      >
        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border-hair)' }}>
          <Icon d={ICONS.spark} size={16} color="var(--ai)" />
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Ask Machine Intelligence or jump to a machine…"
            style={{ flex: 1, border: 'none', outline: 'none', fontFamily: 'var(--font-sans)', fontSize: 14, color: 'var(--ink-900)', background: 'transparent' }}
          />
          <kbd style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-300)', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 4, padding: '2px 6px' }}>Esc</kbd>
        </div>

        {/* AI section */}
        {query && (
          <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border-hair)' }}>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--ai)', marginBottom: 6 }}>Ask Machine Intelligence</div>
            <button
              onClick={() => { onClose(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', borderRadius: 'var(--r-md)', border: 'none', background: 'var(--ai-soft)', cursor: 'pointer', textAlign: 'left' }}
            >
              <Icon d={ICONS.spark} size={14} color="var(--ai)" />
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ai-ink)' }}>{query}</span>
            </button>
          </div>
        )}

        {/* Machines section */}
        <div style={{ padding: '10px 18px', maxHeight: 300, overflowY: 'auto' }}>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--ink-500)', marginBottom: 6 }}>Jump to machine</div>
          {filtered.length === 0 && (
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-300)', padding: '8px 0' }}>No machines found</div>
          )}
          {filtered.slice(0, 8).map((m, i) => {
            const isActive = m.Machines_status === 'Active';
            return (
              <button
                key={i}
                onClick={() => { navigate(`/app/submachine/${m.Machine_name}/${m.Machine_ID}`); onClose(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', borderRadius: 'var(--r-md)', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', marginBottom: 2 }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: isActive ? 'var(--online)' : 'var(--offline)', animation: isActive ? 'atoms-pulse 2s infinite' : 'none' }} />
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-900)', fontWeight: 500 }}>{m.Machine_name}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)' }}>{m.Machine_ID}</span>
              </button>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Live mono clock ───────────────────────────────────────────────────────────
function LiveClock() {
  const [time, setTime] = useState(() => new Date().toLocaleTimeString('en-GB'));
  useEffect(() => {
    const id = setInterval(() => setTime(new Date().toLocaleTimeString('en-GB')), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span style={{
      fontFamily: 'var(--font-mono)', fontSize: 12,
      color: 'var(--ink-500)', letterSpacing: 0.5,
    }}>
      {time}
    </span>
  );
}
